// PracticeReviewTab.jsx — Dan's side of Practice Studio, rendered as the
// 🎓 Practice tab inside /library. Three jobs:
//   1. Review reps — rate 1–5 ⭐ + optional note. The rating IS the XP bonus
//      (rating × PRACTICE_XP.perRatingStar, derived — see utils/practice.js).
//   2. Manage artist accounts — staffRoles/{email} docs ("artist" role).
//      Creating one is what lets that Google login into the PWA at all.
//   3. Curate the training ladder — config/practiceLadder.steps[].
//
// All writes here are admin-only at the rules layer; artists never see this
// view (their only route is /practice).

import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection, onSnapshot, doc, updateDoc, setDoc, deleteDoc, getDocs, query, where,
} from "firebase/firestore";
import useFlashDesigns from "../hooks/useFlashDesigns";
import useArtists, { artistSlug, artistNameKeys } from "../hooks/useArtists";
import PracticeAnalytics from "./PracticeAnalytics";
import ArtistAvatar, { avatarFrameForXp } from "../components/ArtistAvatar";
import {
  formatDrawTime, PRACTICE_XP, deriveLiveStats, rankForXp, repTotalXp, deriveBadges,
} from "../utils/practice";
import { STAFF_THEME, FONT } from "../theme/staff";

const BRAND = STAFF_THEME;

export default function PracticeReviewTab() {
  const { allDesigns, designMap } = useFlashDesigns(null);
  const { allArtists } = useArtists();
  const [reps, setReps] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [filter, setFilter] = useState("unreviewed"); // unreviewed | all
  const [view, setView] = useState("review"); // review | analytics

  useEffect(() => {
    return onSnapshot(collection(db, "practiceReps"), (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setReps(rows);
    });
  }, []);

  // One-shot shift summaries — powers retroactive live XP on artist cards and
  // the "found in shift history" registry suggestions.
  useEffect(() => {
    let cancelled = false;
    getDocs(collection(db, "shift_summaries"))
      .then((snap) => { if (!cancelled) setSummaries(snap.docs.map((d) => d.data())); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const unreviewed = reps.filter((r) => !r.reviewed);
  const shown = filter === "unreviewed" ? unreviewed : reps;

  const analyticsMode = view === "analytics";
  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "1rem", color: BRAND.text, fontFamily: FONT.body }}>
      <div style={{ maxWidth: "720px", margin: "0 auto" }}>

        {/* View toggle — review workflow vs analytics lens */}
        <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.8rem" }}>
          <FilterChip active={!analyticsMode} onClick={() => setView("review")} label="📋 Review" />
          <FilterChip active={analyticsMode} onClick={() => setView("analytics")} label="📊 Analytics" />
        </div>

        {analyticsMode ? (
          <PracticeAnalytics artists={allArtists.filter((a) => a.active !== false)} reps={reps} summaries={summaries} />
        ) : (
        <>
        <ArtistCards artists={allArtists} reps={reps} summaries={summaries} designMap={designMap} />
        <ArtistsManager artists={allArtists} summaries={summaries} />
        <LadderEditor allDesigns={allDesigns} designMap={designMap} />

        {/* Rep review feed */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", margin: "1.2rem 0 0.7rem" }}>
          <div style={sectionLabelStyle()}>
            Reps to review {unreviewed.length > 0 && (
              <span style={{
                background: "rgba(239,83,126,0.9)", color: "white", fontSize: "0.68rem",
                fontWeight: 800, padding: "0.1rem 0.45rem", borderRadius: "999px", marginLeft: "0.3rem",
              }}>{unreviewed.length}</span>
            )}
          </div>
          <div style={{ flex: 1 }} />
          <FilterChip active={filter === "unreviewed"} onClick={() => setFilter("unreviewed")} label="Unreviewed" />
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="All" />
        </div>

        {shown.length === 0 ? (
          <div style={{ color: BRAND.dim, fontSize: "0.85rem", padding: "1rem 0" }}>
            {filter === "unreviewed" ? "All caught up — nothing awaiting review. 🎉" : "No practice reps yet."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>
            {shown.map((r) => (
              <RepReviewCard key={r.id} rep={r} design={r.designId ? designMap[r.designId] : null} />
            ))}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function RepReviewCard({ rep, design }) {
  const [rating, setRating] = useState(rep.review?.rating || 0);
  const [note, setNote] = useState(rep.review?.note || "");
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(false);

  const dirty = rating !== (rep.review?.rating || 0) || note !== (rep.review?.note || "");
  const when = rep.createdAt?.seconds
    ? new Date(rep.createdAt.seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "";

  async function save() {
    if (!rating) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "practiceReps", rep.id), {
        reviewed: true,
        // reviewedAt is epoch ms (lives inside a map — same rule as addOns[])
        review: { rating, note: note.trim() || null, reviewedAt: Date.now() },
      });
    } catch (err) {
      alert(`Couldn't save review: ${err?.message || err}`);
    }
    setSaving(false);
  }

  return (
    <div style={{
      background: BRAND.panel, border: `1px solid ${rep.reviewed ? BRAND.border : "rgba(252,179,31,0.5)"}`,
      borderRadius: "14px", padding: "0.8rem",
    }}>
      <div style={{ display: "flex", gap: "0.8rem" }}>
        {/* Attempt photo */}
        <div
          onClick={() => rep.photoUrl && setZoom((z) => !z)}
          style={{
            width: zoom ? "260px" : "96px", height: zoom ? "260px" : "96px",
            borderRadius: "10px", overflow: "hidden", background: "rgba(0,0,0,0.4)",
            flexShrink: 0, cursor: rep.photoUrl ? "zoom-in" : "default",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "width 0.2s, height 0.2s",
          }}
        >
          {rep.photoUrl
            ? <img src={rep.photoUrl} alt="attempt" style={{ width: "100%", height: "100%", objectFit: zoom ? "contain" : "cover" }} />
            : <span style={{ fontSize: "1.6rem" }}>{rep.medium === "griddle" ? "🥞" : "📝"}</span>}
        </div>
        {/* What they were drawing: flash design, or their freeform reference */}
        <div style={{ width: "96px", height: "96px", borderRadius: "10px", overflow: "hidden", background: "rgba(0,0,0,0.4)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {(design?.imageUrl || rep.referenceUrl)
            ? <img src={design?.imageUrl || rep.referenceUrl} alt={design?.label || "reference"} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            : <span style={{ fontSize: "1.3rem" }}>🎨</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: "0.9rem", fontFamily: FONT.display }}>
            {rep.designLabel || "Freeform"}
          </div>
          <div style={{ color: BRAND.muted, fontSize: "0.75rem", marginTop: "0.15rem" }}>
            {rep.artistName} · {rep.medium === "griddle" ? "🥞 griddle" : "📝 paper"} · {formatDrawTime(rep.drawTimeMs)}{when && ` · ${when}`}
          </div>
          {rep.promptText && (
            <div style={{ color: BRAND.pink, fontSize: "0.72rem", marginTop: "0.15rem" }}>🎲 {rep.promptText}</div>
          )}
          {/* Stars */}
          <div style={{ marginTop: "0.4rem", display: "flex", gap: "0.15rem" }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setRating(n === rating ? 0 : n)}
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  fontSize: "1.25rem", padding: "0 0.05rem",
                  filter: n <= rating ? "none" : "grayscale(1) opacity(0.35)",
                }}
                aria-label={`${n} star${n === 1 ? "" : "s"}`}
              >
                ⭐
              </button>
            ))}
            {rating > 0 && (
              <span style={{ color: BRAND.gold, fontSize: "0.72rem", fontWeight: 800, alignSelf: "center", marginLeft: "0.3rem", fontFamily: FONT.display }}>
                +{rating * PRACTICE_XP.perRatingStar} XP
              </span>
            )}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.6rem" }}>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note for the artist (optional)…"
          style={{
            flex: 1, background: "rgba(0,0,0,0.3)", border: `1px solid ${BRAND.border}`,
            borderRadius: "8px", color: BRAND.text, padding: "0.45rem 0.6rem",
            fontSize: "0.8rem", fontFamily: FONT.body,
          }}
        />
        <button
          onClick={save}
          disabled={!rating || !dirty || saving}
          style={{
            background: rating && dirty ? BRAND.gold : BRAND.raised,
            color: rating && dirty ? BRAND.dark : BRAND.dim,
            border: "none", borderRadius: "8px", padding: "0.45rem 0.9rem",
            fontSize: "0.8rem", fontWeight: 800, cursor: rating && dirty ? "pointer" : "default",
            fontFamily: FONT.display,
          }}
        >
          {saving ? "…" : rep.reviewed ? "Update" : "Save review"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Artist cards — "where is everyone at": rank + combined XP per registry
// artist, all derived (reps grouped by artistId + name-matched live stats).
// ─────────────────────────────────────────────────────────────────────────────
function ArtistCards({ artists, reps, summaries, designMap }) {
  const [openId, setOpenId] = useState(null);
  const active = artists.filter((a) => a.active !== false);
  if (active.length === 0) return null;
  const openArtist = openId ? active.find((a) => a.id === openId) : null;
  return (
    <div style={{ marginBottom: "0.7rem" }}>
      <div style={{ ...sectionLabelStyle(), marginBottom: "0.5rem" }}>Artists — where everyone's at</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: "0.6rem" }}>
        {active.map((a) => {
          const mine = reps.filter((r) => r.artistId === a.id);
          const practiceXp = mine.reduce((s, r) => s + repTotalXp(r), 0);
          const live = deriveLiveStats(artistNameKeys(a), summaries);
          const total = practiceXp + live.xp;
          const rank = rankForXp(total).current;
          return (
            <button key={a.id} onClick={() => setOpenId(a.id)} style={{
              background: BRAND.panel, border: `1px solid ${BRAND.border}`,
              borderRadius: "14px", padding: "0.7rem 0.8rem", cursor: "pointer",
              textAlign: "left", color: BRAND.text, fontFamily: FONT.body,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <ArtistAvatar avatar={a.avatar} fallbackEmoji={a.emoji || rank.icon} size={38} frame={avatarFrameForXp(total)} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 800, fontFamily: FONT.display, fontSize: "0.92rem" }}>{a.name}</div>
                  <div style={{ color: BRAND.gold, fontSize: "0.72rem", fontWeight: 700 }}>{rank.icon} {rank.name}</div>
                </div>
                <span style={{ color: BRAND.dim, fontSize: "0.85rem" }}>›</span>
              </div>
              <div style={{ color: BRAND.muted, fontSize: "0.72rem", marginTop: "0.45rem", lineHeight: 1.5 }}>
                <b style={{ color: BRAND.text }}>{total} XP</b> · 📝 {mine.length} rep{mine.length === 1 ? "" : "s"} · 🥞 {live.totalOrders} live
                {live.shiftsWorked > 0 && ` · ${live.shiftsWorked} shift${live.shiftsWorked === 1 ? "" : "s"}`}
                {` · 🎖 ${deriveBadges(mine, live).filter((b) => b.earned).length}`}
              </div>
              <div style={{ color: BRAND.dim, fontSize: "0.68rem", marginTop: "0.2rem" }}>
                {a.email ? `🔓 login: ${a.email}` : "no login yet"}
              </div>
            </button>
          );
        })}
      </div>
      {openArtist && (
        <ArtistDetail
          artist={openArtist}
          reps={reps.filter((r) => r.artistId === openArtist.id)}
          live={deriveLiveStats(artistNameKeys(openArtist), summaries)}
          designMap={designMap}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Artist dossier — history, development trend, and where-to-focus signals.
// Everything derived on the fly from the artist's reps + live stats.
// ─────────────────────────────────────────────────────────────────────────────
function ArtistDetail({ artist, reps, live, designMap, onClose }) {
  // The artist's real booth pancakes — matched by name (original casing:
  // order.artistName is stored verbatim). Equality-only queries, no index.
  const [liveWork, setLiveWork] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const names = [artist.name, ...(artist.aliases || [])].filter(Boolean);
        const rows = [];
        for (const n of names.slice(0, 5)) {
          const snap = await getDocs(query(
            collection(db, "orders"),
            where("artistName", "==", n),
            where("state", "==", "done"),
          ));
          snap.docs.forEach((d) => rows.push(d.data()));
        }
        if (cancelled) return;
        setLiveWork(rows
          .filter((o) => (o.cutoutThumbnailUrl || o.photo_cutout_url || o.thumbnailUrl || o.photo_url) && !o.hidden && !o.trashedAt && !o._isTestData)
          .map((o) => ({
            type: "live",
            url: o.cutoutThumbnailUrl || o.photo_cutout_url || o.thumbnailUrl || o.photo_url,
            t: (o.photographedAt?.seconds || o.completedAt?.seconds || 0) * 1000,
            label: o.designLabel || o.subjectName || "pancake",
            drawMs: o.drawingTimeMs || null,
          })));
      } catch { /* staff-only view; fail quiet */ }
    })();
    return () => { cancelled = true; };
  }, [artist.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const practiceXp = reps.reduce((s, r) => s + repTotalXp(r), 0);
  const total = practiceXp + live.xp;
  const rank = rankForXp(total).current;
  const badges = deriveBadges(reps, live).filter((b) => b.earned);
  const now = Date.now();

  // Per-design aggregates (freeform grouped as its own row)
  const byDesign = {};
  for (const r of reps) {
    const key = r.designId || "__freeform";
    (byDesign[key] = byDesign[key] || []).push(r);
  }
  const rows = Object.entries(byDesign).map(([key, rs]) => {
    const sorted = [...rs].sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
    const rated = sorted.filter((r) => r.review?.rating);
    const draws = sorted.filter((r) => r.drawTimeMs > 0).map((r) => r.drawTimeMs);
    const avgOf = (arr) => arr.reduce((s, v) => s + v, 0) / arr.length;
    let trend = null;
    if (draws.length >= 4) {
      const firstAvg = avgOf(draws.slice(0, 3));
      const lastAvg = avgOf(draws.slice(-3));
      trend = lastAvg < firstAvg * 0.9 ? "faster" : lastAvg > firstAvg * 1.1 ? "slower" : "steady";
    }
    return {
      key,
      label: key === "__freeform" ? "🎨 Freeform" : designMap[key]?.label || rs[0].designLabel || key,
      thumb: key === "__freeform" ? null : designMap[key]?.imageUrl,
      paper: sorted.filter((r) => r.medium !== "griddle").length,
      griddle: sorted.filter((r) => r.medium === "griddle").length,
      avgRating: rated.length ? avgOf(rated.map((r) => r.review.rating)) : null,
      bestRating: rated.reduce((b, r) => Math.max(b, r.review.rating), 0),
      ratedCount: rated.length,
      trend,
      lastMs: draws.length ? draws[draws.length - 1] : null,
      lastAt: sorted[sorted.length - 1].createdAt?.seconds ? sorted[sorted.length - 1].createdAt.seconds * 1000 : null,
      total: sorted.length,
    };
  }).sort((a, b) => b.total - a.total);

  // Where to focus — honest, derived coaching signals (max 4)
  const focus = [];
  const unreviewed = reps.filter((r) => !r.reviewed).length;
  if (unreviewed > 0) focus.push(`⏳ ${unreviewed} rep${unreviewed === 1 ? "" : "s"} awaiting your review — ratings are their feedback loop`);
  for (const row of rows) {
    if (row.ratedCount >= 2 && row.avgRating < 3) focus.push(`🎯 ${row.label}: ratings averaging ${row.avgRating.toFixed(1)}★ — worth a coaching note`);
  }
  for (const row of rows) {
    if (row.key !== "__freeform" && row.paper >= 5 && row.griddle === 0) focus.push(`🥞 ${row.label}: ${row.paper} paper reps, none on batter — time for the griddle`);
  }
  const lastRepAt = reps.reduce((m, r) => Math.max(m, r.createdAt?.seconds ? r.createdAt.seconds * 1000 : 0), 0);
  if (lastRepAt && now - lastRepAt > 7 * 86400000) focus.push(`📅 no practice in ${Math.floor((now - lastRepAt) / 86400000)} days — a nudge might help`);
  if (reps.length >= 10 && live.totalOrders === 0) focus.push("🏪 double digits in reps and no booth pancakes yet — ready for a live debut?");

  // Weekly activity — reps per week, last 8 weeks
  const weeks = Array.from({ length: 8 }, (_, i) => {
    const end = now - i * 7 * 86400000;
    const start = end - 7 * 86400000;
    return reps.filter((r) => {
      const t = r.createdAt?.seconds ? r.createdAt.seconds * 1000 : 0;
      return t > start && t <= end;
    }).length;
  }).reverse();
  const maxWeek = Math.max(1, ...weeks);


  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 70,
      display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: BRAND.bg, border: `1px solid ${BRAND.border}`, borderRadius: "18px",
        width: "100%", maxWidth: "560px", maxHeight: "88vh", overflowY: "auto", padding: "1.1rem",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.7rem", marginBottom: "0.6rem" }}>
          <ArtistAvatar avatar={artist.avatar} fallbackEmoji={artist.emoji || rank.icon} size={52} frame={avatarFrameForXp(total)} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: FONT.display, fontWeight: 800, fontSize: "1.2rem" }}>{artist.name}</div>
            <div style={{ color: BRAND.gold, fontSize: "0.78rem", fontWeight: 700 }}>
              {rank.icon} {rank.name} · {total} XP
              <span style={{ color: BRAND.muted, fontWeight: 600 }}> (📝 {practiceXp} + 🥞 {live.xp})</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: `1px solid ${BRAND.border}`, color: BRAND.muted, borderRadius: "8px", padding: "0.3rem 0.6rem", cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ color: BRAND.muted, fontSize: "0.76rem", marginBottom: "0.8rem" }}>
          {reps.length} practice rep{reps.length === 1 ? "" : "s"} · {live.totalOrders} real pancake{live.totalOrders === 1 ? "" : "s"}
          {live.shiftsWorked > 0 && ` across ${live.shiftsWorked} shift${live.shiftsWorked === 1 ? "" : "s"}`}
          {badges.length > 0 && ` · 🎖 ${badges.map((b) => b.icon).join(" ")}`}
        </div>

        {/* Where to focus */}
        {focus.length > 0 && (
          <div style={{ marginBottom: "0.9rem" }}>
            <div style={{ ...sectionLabelStyle(), marginBottom: "0.35rem" }}>Where to focus</div>
            {focus.slice(0, 4).map((f, i) => (
              <div key={i} style={{
                background: "rgba(252,179,31,0.08)", border: "1px solid rgba(252,179,31,0.3)",
                borderRadius: "10px", padding: "0.4rem 0.6rem", fontSize: "0.76rem",
                marginBottom: "0.3rem", lineHeight: 1.4,
              }}>
                {f}
              </div>
            ))}
          </div>
        )}

        {/* Weekly activity */}
        {reps.length > 0 && (
          <div style={{ marginBottom: "0.9rem" }}>
            <div style={{ ...sectionLabelStyle(), marginBottom: "0.35rem" }}>Activity — last 8 weeks</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: "4px", height: "44px" }}>
              {weeks.map((n, i) => (
                <div key={i} title={`${n} reps`} style={{
                  flex: 1, borderRadius: "3px 3px 0 0",
                  height: `${Math.max(8, (n / maxWeek) * 100)}%`,
                  background: n > 0 ? `linear-gradient(180deg, ${BRAND.gold}, ${BRAND.orange})` : "rgba(255,255,255,0.08)",
                }} />
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", color: BRAND.dim, fontSize: "0.62rem", marginTop: "0.2rem" }}>
              <span>8 wks ago</span><span>this week</span>
            </div>
          </div>
        )}

        {/* Per-design skill table */}
        {rows.length > 0 && (
          <div style={{ marginBottom: "0.9rem" }}>
            <div style={{ ...sectionLabelStyle(), marginBottom: "0.35rem" }}>Designs</div>
            {rows.map((row) => (
              <div key={row.key} style={{
                display: "flex", alignItems: "center", gap: "0.55rem",
                padding: "0.35rem 0", borderBottom: "1px solid rgba(74,164,225,0.12)",
              }}>
                <div style={{ width: "36px", height: "36px", borderRadius: "8px", overflow: "hidden", background: "rgba(0,0,0,0.35)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {row.thumb ? <img src={row.thumb} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <span>🎨</span>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "0.8rem", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.label}</div>
                  <div style={{ color: BRAND.muted, fontSize: "0.68rem" }}>
                    📝 {row.paper} · 🥞 {row.griddle}
                    {row.avgRating != null && ` · avg ${row.avgRating.toFixed(1)}★ (best ${row.bestRating}★)`}
                    {row.lastMs != null && ` · last ${formatDrawTime(row.lastMs)}`}
                  </div>
                </div>
                {row.trend && (
                  <span style={{
                    fontSize: "0.66rem", fontWeight: 800, flexShrink: 0,
                    color: row.trend === "faster" ? "#7dd87d" : row.trend === "slower" ? BRAND.pink : BRAND.dim,
                  }}>
                    {row.trend === "faster" ? "📉 faster" : row.trend === "slower" ? "📈 slower" : "→ steady"}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Development timeline — practice + booth work, oldest → newest */}
        <DevelopmentTimeline reps={reps} liveWork={liveWork} />
      </div>
    </div>
  );
}

// Chronological stream of the artist's work: practice reps and real booth
// pancakes interleaved oldest → newest, grouped by month — the growth story.
function DevelopmentTimeline({ reps, liveWork }) {
  const items = [
    ...reps.map((r) => ({
      type: "rep",
      url: r.photoUrl || null,
      t: r.createdAt?.seconds ? r.createdAt.seconds * 1000 : 0,
      medium: r.medium,
      rating: r.review?.rating || 0,
      label: r.designLabel || "Freeform",
    })),
    ...liveWork,
  ]
    .filter((i) => i.t > 0)
    .sort((a, b) => a.t - b.t)
    .slice(-80); // cap for very long histories — the recent arc matters most

  if (items.length === 0) return null;

  // Group by month
  const groups = [];
  for (const it of items) {
    const key = new Date(it.t).toLocaleDateString("en-US", { month: "long", year: "numeric" });
    const g = groups[groups.length - 1];
    if (g && g.key === key) g.items.push(it);
    else groups.push({ key, items: [it] });
  }

  return (
    <div>
      <div style={{ ...sectionLabelStyle(), marginBottom: "0.35rem" }}>
        Development timeline <span style={{ color: BRAND.dim, textTransform: "none", letterSpacing: 0 }}>· oldest → newest · gold ring = real booth pancake</span>
      </div>
      {groups.map((g) => (
        <div key={g.key} style={{ marginBottom: "0.6rem" }}>
          <div style={{ color: BRAND.dim, fontSize: "0.66rem", fontWeight: 700, margin: "0.3rem 0 0.3rem" }}>{g.key}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
            {g.items.map((it, i) => (
              <div key={i} title={`${it.label}${it.type === "live" ? " · real booth pancake" : it.medium === "griddle" ? " · griddle rep" : " · paper rep"}${it.drawMs ? ` · ${formatDrawTime(it.drawMs)}` : ""}`}
                style={{ width: "76px", textAlign: "center" }}>
                <div style={{
                  width: "76px", height: "76px", borderRadius: "12px", overflow: "hidden",
                  background: "rgba(0,0,0,0.35)", position: "relative",
                  border: it.type === "live" ? `2px solid ${BRAND.gold}` : "2px solid transparent",
                  boxSizing: "border-box",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {it.url
                    ? <img src={it.url} alt={it.label} style={{ width: "100%", height: "100%", objectFit: it.type === "live" ? "contain" : "cover" }} />
                    : <span style={{ fontSize: "1.3rem" }}>{it.medium === "griddle" ? "🥞" : "📝"}</span>}
                  {it.type === "live" && (
                    <span style={{
                      position: "absolute", top: "2px", right: "2px",
                      background: BRAND.gold, color: "#0d1833", fontSize: "0.52rem",
                      fontWeight: 800, borderRadius: "5px", padding: "0.05rem 0.25rem",
                    }}>LIVE</span>
                  )}
                </div>
                <div style={{ color: BRAND.dim, fontSize: "0.58rem", marginTop: "0.12rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {it.type === "live" ? it.label : it.rating ? "⭐".repeat(it.rating) : it.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Registry manager — artists/{id} docs. Email is a PROPERTY of an artist:
// setting it writes the staffRoles/{email} login pointer; clearing/removing
// deletes it. Historical artistName strings from shift summaries surface as
// one-tap "add from history" suggestions so past pancakes tether cleanly.
// ─────────────────────────────────────────────────────────────────────────────
function ArtistsManager({ artists, summaries }) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const registryNameKeys = new Set(artists.flatMap((a) => artistNameKeys(a)));
  const historySuggestions = useMemo(() => {
    const names = new Map(); // lowercase → display form
    for (const s of summaries) {
      if (s._isTestData) continue;
      for (const n of Object.keys(s.artistBreakdown || {})) {
        const key = n.trim().toLowerCase();
        if (!key || key === "unknown" || registryNameKeys.has(key)) continue;
        if (!names.has(key)) names.set(key, n.trim());
      }
    }
    return [...names.values()];
  }, [summaries, artists]); // eslint-disable-line react-hooks/exhaustive-deps

  async function addArtist(name) {
    const clean = name.trim();
    if (!clean) return;
    setBusy(true);
    try {
      await setDoc(doc(db, "artists", artistSlug(clean)), {
        name: clean,
        email: null,
        aliases: [],
        active: true,
        addedAt: Date.now(),
      }, { merge: true });
      setNewName("");
    } catch (err) {
      alert(`Couldn't add artist: ${err?.message || err}`);
    }
    setBusy(false);
  }

  return (
    <Collapsible open={open} setOpen={setOpen} title={`👥 Artists (${artists.filter((a) => a.active !== false).length})`}>
      <div style={{ color: BRAND.muted, fontSize: "0.76rem", marginBottom: "0.6rem", lineHeight: 1.45 }}>
        The one roster everything shares — shift-open picker, pancake credits,
        Practice Studio. Set an email to let that Google account sign in and see
        <b> only</b> the Practice Studio; clear it to revoke login (their history stays).
      </div>
      {artists.map((a) => (
        <ArtistRow key={a.id} artist={a} />
      ))}
      {historySuggestions.length > 0 && (
        <div style={{ marginTop: "0.6rem" }}>
          <div style={{ color: BRAND.dim, fontSize: "0.7rem", marginBottom: "0.3rem" }}>Found in shift history:</div>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
            {historySuggestions.map((n) => (
              <button key={n} onClick={() => addArtist(n)} disabled={busy} style={{
                background: "rgba(252,179,31,0.12)", border: `1px solid ${BRAND.gold}`,
                color: BRAND.gold, borderRadius: "999px", padding: "0.25rem 0.7rem",
                fontSize: "0.76rem", fontWeight: 700, cursor: "pointer",
              }}>
                + {n}
              </button>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.6rem" }}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addArtist(newName)}
          placeholder="New artist name…"
          style={inputStyle({ flex: 1 })}
        />
        <button onClick={() => addArtist(newName)} disabled={busy} style={smallPrimary()}>+ Add</button>
      </div>
    </Collapsible>
  );
}

function ArtistRow({ artist }) {
  const [editingEmail, setEditingEmail] = useState(false);
  const [email, setEmail] = useState(artist.email || "");
  const [busy, setBusy] = useState(false);

  async function saveEmail() {
    const clean = email.trim().toLowerCase();
    if (clean && !clean.includes("@")) { alert("That doesn't look like an email."); return; }
    setBusy(true);
    try {
      const old = (artist.email || "").toLowerCase();
      if (old && old !== clean) await deleteDoc(doc(db, "staffRoles", old));
      if (clean) {
        await setDoc(doc(db, "staffRoles", clean), {
          role: "artist",
          displayName: artist.name,
          artistId: artist.id,
          addedAt: Date.now(),
        });
      }
      await updateDoc(doc(db, "artists", artist.id), { email: clean || null });
      setEditingEmail(false);
    } catch (err) {
      alert(`Couldn't save: ${err?.message || err}`);
    }
    setBusy(false);
  }

  async function removeArtist() {
    if (!window.confirm(`Remove ${artist.name} from the registry? Their pancakes and reps keep their name; login (if any) is revoked.`)) return;
    setBusy(true);
    try {
      if (artist.email) await deleteDoc(doc(db, "staffRoles", artist.email.toLowerCase()));
      await deleteDoc(doc(db, "artists", artist.id));
    } catch (err) {
      alert(`Couldn't remove: ${err?.message || err}`);
    }
    setBusy(false);
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.35rem 0", flexWrap: "wrap" }}>
      <div style={{ flex: 1, fontSize: "0.84rem", minWidth: "120px" }}>
        <b>{artist.emoji ? `${artist.emoji} ` : ""}{artist.name}</b>{" "}
        {!editingEmail && (
          <span style={{ color: BRAND.muted }}>
            · {artist.email || "no login"}
          </span>
        )}
      </div>
      {editingEmail ? (
        <>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="google email (blank = no login)"
            style={inputStyle({ flex: 2, minWidth: "170px" })}
          />
          <button onClick={saveEmail} disabled={busy} style={smallPrimary()}>Save</button>
          <button onClick={() => { setEditingEmail(false); setEmail(artist.email || ""); }} style={tinyBtn()}>✕</button>
        </>
      ) : (
        <>
          <button onClick={() => setEditingEmail(true)} style={tinyBtn()}>
            {artist.email ? "✎ email" : "+ login"}
          </button>
          <button onClick={removeArtist} disabled={busy} style={smallDanger()}>Remove</button>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function LadderEditor({ allDesigns, designMap }) {
  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState(null); // null until doc loads
  const [savedSteps, setSavedSteps] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    return onSnapshot(doc(db, "config", "practiceLadder"), (snap) => {
      const s = snap.exists() && Array.isArray(snap.data().steps) ? snap.data().steps : [];
      setSavedSteps(s);
      setSteps((cur) => (cur === null ? s.map((x) => ({ ...x })) : cur));
    });
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(steps) !== JSON.stringify(savedSteps),
    [steps, savedSteps]
  );

  function patch(i, field, value) {
    setSteps((cur) => cur.map((s, j) => (j === i ? { ...s, [field]: value } : s)));
  }
  function move(i, dir) {
    setSteps((cur) => {
      const next = [...cur];
      const j = i + dir;
      if (j < 0 || j >= next.length) return cur;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  async function save() {
    setSaving(true);
    try {
      await setDoc(doc(db, "config", "practiceLadder"), { steps, updatedAt: Date.now() });
    } catch (err) {
      alert(`Couldn't save ladder: ${err?.message || err}`);
    }
    setSaving(false);
  }

  if (steps === null) return null;

  return (
    <Collapsible open={open} setOpen={setOpen} title={`🪜 Training Ladder (${steps.length} step${steps.length === 1 ? "" : "s"})`}>
      <div style={{ color: BRAND.muted, fontSize: "0.76rem", marginBottom: "0.6rem", lineHeight: 1.45 }}>
        The artist sees these in order with a "start here" highlight. A step
        completes when its rep counts are met — and, if you set a min ⭐, once
        you've rated one of their reps that high. Every rep pays XP the moment
        it's logged (📝 {PRACTICE_XP.paperRep} / 🥞 {PRACTICE_XP.griddleRep}, + your rating × {PRACTICE_XP.perRatingStar});
        the XP column here is the <b>bonus</b> for finishing the whole step.
      </div>
      {steps.map((s, i) => (
        <div key={i} style={{
          display: "flex", alignItems: "center", gap: "0.45rem", flexWrap: "wrap",
          padding: "0.45rem 0", borderBottom: `1px solid ${BRAND.divider || BRAND.border}`,
        }}>
          <span style={{ color: BRAND.dim, fontSize: "0.75rem", width: "1.1rem" }}>{i + 1}.</span>
          <select
            value={s.designId || ""}
            onChange={(e) => patch(i, "designId", e.target.value)}
            style={inputStyle({ minWidth: "130px" })}
          >
            <option value="">— design —</option>
            {allDesigns.map((d) => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>
          <NumField label="📝" value={s.paperReps} onChange={(v) => patch(i, "paperReps", v)} />
          <NumField label="🥞" value={s.griddleReps} onChange={(v) => patch(i, "griddleReps", v)} />
          <NumField label="⭐≥" value={s.minRating} onChange={(v) => patch(i, "minRating", Math.min(5, v))} />
          <NumField label="XP" value={s.xp ?? PRACTICE_XP.ladderStep} onChange={(v) => patch(i, "xp", v)} wide />
          <button onClick={() => move(i, -1)} style={tinyBtn()}>↑</button>
          <button onClick={() => move(i, 1)} style={tinyBtn()}>↓</button>
          <button onClick={() => setSteps((cur) => cur.filter((_, j) => j !== i))} style={smallDanger()}>✕</button>
        </div>
      ))}
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.6rem" }}>
        <button
          onClick={() => setSteps((cur) => [...cur, { designId: "", paperReps: 3, griddleReps: 1, minRating: 0, xp: PRACTICE_XP.ladderStep }])}
          style={smallPrimary()}
        >
          + Add step
        </button>
        <div style={{ flex: 1 }} />
        {dirty && (
          <button onClick={save} disabled={saving || steps.some((s) => !s.designId)} style={smallPrimary()}>
            {saving ? "…" : "Save ladder"}
          </button>
        )}
      </div>
    </Collapsible>
  );
}

function NumField({ label, value, onChange, wide }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: "0.2rem", fontSize: "0.75rem", color: BRAND.muted }}>
      {label}
      <input
        type="number"
        min="0"
        value={value ?? 0}
        onChange={(e) => onChange(Math.max(0, parseInt(e.target.value, 10) || 0))}
        style={inputStyle({ width: wide ? "58px" : "44px", padding: "0.3rem 0.35rem" })}
      />
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function Collapsible({ open, setOpen, title, children }) {
  return (
    <div style={{
      background: BRAND.panel, border: `1px solid ${BRAND.border}`,
      borderRadius: "14px", marginBottom: "0.7rem", overflow: "hidden",
    }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", background: "transparent", border: "none", color: BRAND.text,
          fontFamily: FONT.display, fontWeight: 800, fontSize: "0.9rem",
          padding: "0.7rem 0.9rem", cursor: "pointer", textAlign: "left",
          display: "flex", alignItems: "center",
        }}
      >
        <span style={{ flex: 1 }}>{title}</span>
        <span style={{ color: BRAND.dim }}>{open ? "▾" : "▸"}</span>
      </button>
      {open && <div style={{ padding: "0 0.9rem 0.8rem" }}>{children}</div>}
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? BRAND.gold : "transparent",
        color: active ? BRAND.dark : BRAND.muted,
        border: active ? `1px solid ${BRAND.gold}` : `1px solid ${BRAND.border}`,
        borderRadius: "999px", padding: "0.25rem 0.7rem", fontSize: "0.74rem",
        fontWeight: 700, cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function sectionLabelStyle() {
  return {
    color: "rgba(255,218,173,0.55)", fontFamily: FONT.display,
    fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.14em",
    textTransform: "uppercase",
  };
}

function inputStyle(extra = {}) {
  return {
    background: "rgba(0,0,0,0.3)", border: `1px solid ${BRAND.border}`,
    borderRadius: "8px", color: BRAND.text, padding: "0.4rem 0.55rem",
    fontSize: "0.8rem", fontFamily: FONT.body, ...extra,
  };
}

function smallPrimary() {
  return {
    background: BRAND.gold, color: BRAND.dark, border: "none",
    borderRadius: "8px", padding: "0.4rem 0.8rem", fontSize: "0.78rem",
    fontWeight: 800, cursor: "pointer", fontFamily: FONT.display,
  };
}

function smallDanger() {
  return {
    background: "transparent", border: `1px solid rgba(239,83,126,0.5)`,
    color: BRAND.pink, borderRadius: "8px", padding: "0.3rem 0.6rem",
    fontSize: "0.72rem", fontWeight: 700, cursor: "pointer",
  };
}

function tinyBtn() {
  return {
    background: "transparent", border: `1px solid ${BRAND.border}`,
    color: BRAND.muted, borderRadius: "6px", padding: "0.2rem 0.45rem",
    fontSize: "0.72rem", cursor: "pointer",
  };
}
